import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { handleSearchQuery } from '../../utils/utils';

import { Event } from '../../models';

import { FilterSetController, Filter, Card, ContentCard, ToastContext, SortButton, WeezHeader, Paginator } from '@weezevent/nacre';
import { LoadingCard, SauronEmptyCard } from '../../components';
import EventCard from './EventCard';

import css from './events.module.css';

const spec = {
    multiple: ['time_status', 'publication_status__in'],
    fields: ['time_status', 'publication_status__in', 'search', 'ordering']
};

const initialFilters = {
    time_status: new Set(['upcoming', 'ongoing']),
    publication_status__in: new Set(['draft', 'published']),
    ordering: 'start_date'
};

export default function EventList({ organizationId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();

    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 50, offset: 0 }));
    const [pagination, setPagination] = React.useState(() => ({ ...defaultPagination }));
    const [queryParams, setQueryParams] = React.useState(initialFilters);

    const [events, loadingEvents] = Event.useApiModel(
        {
            organizationId,
            strict: true,
            cache: false,
            allow_cache: false,
            query: {
                limit: pagination.limit,
                offset: pagination.offset,
                ...handleSearchQuery(spec, queryParams, true)
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId, pagination, queryParams]
    );

    const sortOptions = React.useMemo(() => {
        const options = [
            { key: 'id', label: t('sauron.events.sorting.id'), value: 'id' },
            { key: 'start_date_asc', label: t('sauron.events.sorting.date_asc'), value: 'start_date' },
            { key: 'start_date_desc', label: t('sauron.events.sorting.date_desc'), value: '-start_date' },
            { key: 'name', label: t('sauron.events.sorting.alphabetical'), value: 'name' }
        ];
        return options;
    }, []);

    const timeStatusOptions = [
        { key: 'upcoming', text: t('sauron.events.time-status.upcoming'), value: 'upcoming', name: 'upcoming' },
        { key: 'ongoing', text: t('sauron.events.time-status.ongoing'), value: 'ongoing', name: 'ongoing' },
        { key: 'over', text: t('sauron.events.time-status.over'), value: 'over', name: 'over' }
    ];

    const publicationStatusOptions = [
        { key: 'published', text: t('sauron.events.publication-status.published'), name: 'published' },
        { key: 'draft', text: t('sauron.events.publication-status.draft'), name: 'draft' },
        { key: 'closed', text: t('sauron.events.publication-status.closed'), name: 'closed' }
    ];

    let resetPagination = React.useCallback(() => {
        setPagination({
            ...defaultPagination
        });
        setCurrentPage(0);
    }, []);

    const handleSort = React.useCallback(value => {
        setQueryParams({ ...queryParams, ordering: value });
        resetPagination();
    });

    let handleSearch = React.useCallback(
        value => {
            const filterSpread = { ...queryParams };
            if (value) {
                filterSpread['search'] = value;
            } else {
                delete filterSpread['search'];
            }
            setQueryParams(filterSpread);
        },
        [queryParams]
    );

    const handlePage = React.useCallback(value => {
        setCurrentPage(value);
        setPagination({ ...pagination, offset: pagination.limit * value });
    }, []);

    const eventsCount = React.useMemo(() => {
        if (loadingEvents) {
            return '--';
        }
        return Number.parseInt(events?.__meta?.headers.get('total-count')) || 0;
    }, [events, loadingEvents]);

    return (
        <>
            <WeezHeader
                title={t('sauron.events.event_plural')}
                backLink={{
                    onClick: () => {
                        history.push(`/organizations`);
                    }
                }}
            >
                {t(`sauron.events.header-${organizationId ? 'orga-' : ''}subtitle`)}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <FilterSetController
                        className={css['filter-block']}
                        total={eventsCount}
                        isLoadingTotal={loadingEvents}
                        labelTotal={eventsCount > 1 ? t('common.events') : t('common.event')}
                        labelFilters={t('sauron.events.filter')}
                        labelReset={t('sauron.events.labelReset')}
                        initialValues={queryParams}
                        defaultValues={initialFilters}
                        onChange={values => {
                            if (queryParams.search !== undefined && queryParams.search.length > 0) {
                                values.search = queryParams.search;
                            } else {
                                delete values.search;
                            }
                            setQueryParams(values);
                        }}
                        onReset={() => setQueryParams(initialFilters)}
                        search={{
                            onChange: (_, value) => handleSearch(value),
                            handleReset: () => handleSearch(''),
                            placeholder: t('sauron.events.search'),
                            value: queryParams.search
                        }}
                        leftComponent={<SortButton label={t('sauron.events.sort')} sortValue={queryParams.ordering} onClick={handleSort} options={sortOptions} />}
                    >
                        <Filter
                            index="1"
                            multiple
                            labelSelectAll={t('sauron.events.time-status.select-all')}
                            labelName={t('sauron.events.time-status.label')}
                            labelCTA={t('sauron.webhooks.filters.label-cta')}
                            name="time_status"
                            options={timeStatusOptions}
                        />
                        <Filter
                            index="2"
                            multiple
                            labelSelectAll={t('sauron.events.time-status.select-all')}
                            labelName={t('sauron.events.publication-status.label')}
                            labelCTA={t('sauron.webhooks.filters.label-cta')}
                            name="publication_status__in"
                            options={publicationStatusOptions}
                        />
                    </FilterSetController>
                </ContentCard>
            </Card>
            <LoadingCard loading={loadingEvents}>
                {events?.length > 0 ? (
                    <>
                        {events.map(event => (
                            <EventCard key={event.id} event={event} />
                        ))}
                        <Paginator style={{ padding: 15 }} currentPage={currentPage} onPageChange={handlePage} pageSize={pagination.limit} totalCount={eventsCount} />
                    </>
                ) : (
                    <SauronEmptyCard title={t('sauron.events.empty-card-title')} subTitle={t('sauron.events.empty-card-subtitle')} />
                )}
            </LoadingCard>
        </>
    );
}
